/** Hook for product sync operations (JTL sync, status, reset, count). */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getSyncStatus, startProductSync, resetSyncStatus, getProductCount } from '@/lib/api/products'

export function useProductSync() {
  const qc = useQueryClient()
  const statusQuery = useQuery({
    queryKey: ['sync-status', 'jtl'],
    queryFn: () => getSyncStatus('jtl'),
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 3000 : false),
  })
  const countQuery = useQuery({ queryKey: ['products-count'], queryFn: () => getProductCount() })

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['sync-status'] })
    qc.invalidateQueries({ queryKey: ['products'] })
    qc.invalidateQueries({ queryKey: ['products-count'] })
  }
  const syncMutation = useMutation({
    mutationFn: (fullSync: boolean) => startProductSync({ provider: 'jtl', full_sync: fullSync }),
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.items_synced ? `${result.items_synced} Produkte synchronisiert` : 'Synchronisation gestartet')
        invalidateAll()
      } else toast.error(result.error || 'Fehler beim Starten')
    },
    onError: () => toast.error('Fehler beim Starten der Synchronisation'),
  })
  const resetMutation = useMutation({
    mutationFn: () => resetSyncStatus('jtl'),
    onSuccess: (result) => {
      if (result.success) { toast.success('Sync-Status zurückgesetzt'); invalidateAll() }
      else toast.error(result.error || 'Fehler beim Zurücksetzen')
    },
    onError: () => toast.error('Fehler beim Zurücksetzen des Sync-Status'),
  })

  return {
    status: statusQuery.data?.status ?? 'idle' as const,
    statusData: statusQuery.data ?? null,
    statusUpdatedAt: statusQuery.dataUpdatedAt,
    count: countQuery.data?.count ?? 0,
    isSyncing: syncMutation.isPending || statusQuery.data?.status === 'running',
    startSync: syncMutation.mutate,
    resetSync: () => resetMutation.mutate(),
    isResetting: resetMutation.isPending,
  }
}
